import { Add, Edit, Remove, WarningAmberTwoTone } from '@mui/icons-material';
import { Avatar, Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { Wallet, WalletLog, WalletLogInfo } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { Lang, useLocale } from '../../contexts/locale.context';
import EventModal from '../../pages/Wallets/EventModal';
import { useCollection } from '../../utils/api';
import { Column } from '../DataTable';
import HistoryModal from './HistoryModal';

const colorMap = {
  income: 'success.main',
  spending: 'error.main',
  transfer: 'warning.main',
  cancel: 'warning.main',
};

type Props = {
  wallet: DbDocument<Wallet>;
};

type Row = DbDocument<WalletLog>;

export const getWalletLogComment = (info: WalletLogInfo, lang: Lang): string => {
  if (info.type === 'order') {
    return `${t.order[lang]} #${info.number}`;
  }
  if (info.type === 'shift') {
    return t.salary_payment[lang];
  }
  if (info.type === 'supply') {
    return `${t.supply[lang]} #${info.number}`;
  }
  if (info.type === 'transfer') {
    return `${info.direction === 'from' ? t.transfer_from_wallet[lang] : t.transfer_to_wallet[lang]} ${
      info.walletName
    }`;
  }
  if (info.type === 'cancelOrder') {
    return `${t.payment_cancellation_message[lang]} #${info.number}`;
  }
  if (info.type === 'cancel') {
    const autocomment = getWalletLogComment(info.canceledLog.info, lang);
    return `${t.cancelled[lang]}: "${
      autocomment ? `${autocomment}. ${info.canceledLog.comment}` : info.canceledLog.comment
    }". ${t.date[lang]}: ${format(info.canceledLog.time, 'dd.MM.yyyy HH:mm')}`;
  }
  return '';
};

const WalletHistory = ({ wallet }: Props) => {
  const [dateRange, setDateRange] = useState<{ end: number; start: number } | null>(null);
  const { lang } = useLocale();
  const { cafeSlug, getCurrency } = useAdmin();
  const { workersMap, worker } = useCafe();
  const [editLog, setEditLog] = useState<DbDocument<WalletLog> | null>(null);

  /** @todo remove */
  const startDate =
    cafeSlug === 'lm64cv5j' && dateRange && dateRange.start < 1711494000000 ? 1711494000000 : dateRange?.start || 0;

  const { data } = useCollection<WalletLog>(dateRange ? `cafes/${cafeSlug}/wallets/${wallet.id}/logs` : null, {
    where: ['time', '<', dateRange?.end || 0],
    where2: ['time', '>', startDate],
    orderBy: ['time', 'desc'],
  });

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'date',
        title: t.time[lang],
        align: 'left',
        render: (r: Row) => <Typography>{format(new Date(r.time), 'HH:mm')}</Typography>,
      },
      {
        name: 'author',
        title: t.author[lang],
        align: 'left',
        render: (r: Row) => (
          <Stack direction="row" alignItems="center" spacing={{ xs: 1 }}>
            <Avatar src={workersMap[r.workerId]?.photoUrl} />
            <span>{workersMap[r.workerId]?.name || r.workerName}</span>
          </Stack>
        ),
      },
      {
        name: 'comment',
        title: t.comment[lang],
        align: 'left',
        render: (r: Row) => {
          const autoComment = getWalletLogComment(r.info, lang);
          return (
            <>
              {r.category && (
                <Box sx={{ textAlign: 'center', mb: 0.5 }}>
                  <Chip size="small" label={t[r.category as keyof typeof t]?.[lang] || r.category} />
                </Box>
              )}
              {r.info.type === 'cancel' && (
                <Box sx={{ textAlign: 'center' }}>
                  <WarningAmberTwoTone color="warning" />
                </Box>
              )}
              {autoComment && (
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'center' }}>
                  {autoComment}
                </Typography>
              )}
              {r.comment && (
                <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'center' }}>
                  {r.comment}
                </Typography>
              )}
            </>
          );
        },
      },
      {
        name: 'amount',
        title: t.amount[lang],
        align: 'left',
        render: (r: Row) =>
          r.info.type === 'cancel' ? (
            ''
          ) : (
            <>
              <Stack direction="row" alignItems="center">
                {r.sum > 0 && <Add color="success" />}
                {r.sum < 0 && <Remove color="error" />}
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{
                    color: r.sum > 0 ? 'success.main' : 'error.main',
                    textDecoration: r.canceled ? 'line-through' : 'none',
                  }}
                >
                  {getCurrency(r.sum > 0 ? r.sum : -r.sum)}
                </Typography>
              </Stack>
              {!!r.bankCommission && (
                <Typography
                  variant="caption"
                  sx={{
                    textDecoration: r.canceled ? 'line-through' : 'none',
                  }}
                >
                  {`${t.commission[lang]}: ${getCurrency(-r.bankCommission)}`}
                </Typography>
              )}
            </>
          ),
      },
      {
        name: 'total',
        title: t.balance[lang],
        align: 'right',
        render: (r: Row) => <Typography>{getCurrency(r.total)}</Typography>,
      },
      {
        name: 'cancel',
        title: '',
        align: 'right',
        render: (r: Row) =>
          r.info.type !== 'cancel' && !r.canceled ? (
            <IconButton onClick={() => setEditLog(r)} disabled={!worker?.credentials.perform_operations_with_wallets}>
              <Edit />
            </IconButton>
          ) : null,
      },
    ],
    [getCurrency, lang, worker?.credentials.perform_operations_with_wallets, workersMap]
  );

  return (
    <>
      <HistoryModal name={wallet.name} columns={columns} rows={data || []} setDateRange={setDateRange} />
      {editLog && <EventModal log={editLog} wallet={wallet} onClose={() => setEditLog(null)} />}
    </>
  );
};

export default WalletHistory;
